import React, { useState, useRef, useEffect } from 'react';
import {
  Box, Typography, Button, Stack, TextField,
  Select, MenuItem, FormControl, InputLabel,
  IconButton, CircularProgress,
} from '@mui/material';
import { Plus, Trash2, Pencil, GripVertical } from 'lucide-react';

const navy = '#1e3a5f';
const inputSx = { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' } };

const getCurrentHebrewYear = () => {
  const now = new Date();
  // Rosh Hashana is in Sep/Oct — rough cutoff at month 9
  return now.getMonth() >= 8 ? now.getFullYear() + 3761 : now.getFullYear() + 3760;
};

const YEAR_OPTIONS = (() => {
  const hy = getCurrentHebrewYear();
  return [hy, hy + 1];
})();

const HEBREW_MONTHS = {
  'Nisan': 'ניסן', 'Nissan': 'ניסן',
  'Iyyar': 'אייר', 'Iyar': 'אייר',
  'Sivan': 'סיון',
  'Tamuz': 'תמוז', 'Tammuz': 'תמוז',
  'Av': 'אב',
  'Elul': 'אלול',
  'Tishrei': 'תשרי', 'Tishri': 'תשרי', "Tish'rei": 'תשרי',
  'Cheshvan': 'חשון', 'Heshvan': 'חשון', 'Marcheshvan': 'חשון', "Che'shvan": 'חשון',
  'Kislev': 'כסלו', "Kis'lev": 'כסלו',
  'Tevet': 'טבת', 'Teves': 'טבת', "Te'vet": 'טבת',
  'Shvat': 'שבט', 'Shevat': 'שבט', "Sh'vat": 'שבט',
  'Adar': 'אדר', 'Adar I': 'אדר א׳', 'Adar II': 'אדר ב׳',
};

const toGematria = (n) => {
  if (n === 15) return 'ט"ו';
  if (n === 16) return 'ט"ז';
  const letters = [
    [400,'ת'],[300,'ש'],[200,'ר'],[100,'ק'],
    [90,'צ'],[80,'פ'],[70,'ע'],[60,'ס'],[50,'נ'],
    [40,'מ'],[30,'ל'],[20,'כ'],[10,'י'],
    [9,'ט'],[8,'ח'],[7,'ז'],[6,'ו'],[5,'ה'],
    [4,'ד'],[3,'ג'],[2,'ב'],[1,'א'],
  ];
  let num = n, result = '';
  for (const [val, letter] of letters) {
    while (num >= val) { result += letter; num -= val; }
  }
  if (result.length === 1) return result + '׳';
  return result.slice(0, -1) + '"' + result.slice(-1);
};

const formatHebrewDate = (hdate) => {
  if (!hdate) return '';
  // hdate format: "3 Cheshvan 5786" or "15 Adar I 5784"
  const parts = hdate.split(' ');
  if (parts.length < 3) return '';
  let day, month, year;
  if (parts.length === 4) {
    // e.g. "15 Adar I 5784"
    day = parts[0];
    month = parts[1] + ' ' + parts[2];
    year = parts[3];
  } else {
    [day, month, year] = parts;
  }
  const hebrewMonth = HEBREW_MONTHS[month] || month;
  const dayNum = parseInt(day, 10);
  const yearNum = parseInt(year, 10);
  if (isNaN(dayNum) || isNaN(yearNum)) return '';
  return `${toGematria(dayNum)} ${hebrewMonth} ${toGematria(yearNum % 1000)}`;
};

const formatParshaDate = (dateStr) => {
  try {
    return new Date(dateStr).toLocaleDateString('he-IL', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return dateStr;
  }
};

const textSnippet = (text, max = 50) => {
  const plain = (text || '').replace(/<[^>]*>/g, '').trim();
  return plain.length > max ? plain.slice(0, max) + '…' : plain;
};

// ─── useParshaList ──────────────────────────────────────────────────────────────

// חילוץ שם בסיסי של חג (ללא מספור/סוגריים) — חייב להתאים ללוגיקה ב-computeShabbatTitle
const getHolidayBaseName = (hebrew, title) => {
  if (hebrew?.includes('פסח') || title?.includes('Pesach'))   return 'פסח';
  if (hebrew?.includes('סוכות') || title?.includes('Sukkot'))  return 'סוכות';
  if (hebrew?.includes('שבועות') || title?.includes('Shavuot')) return 'שבועות';
  if (hebrew?.includes('ראש השנה') || title?.includes('Rosh Hashana')) return 'ראש השנה';
  if (hebrew?.includes('שמיני עצרת') || title?.includes('Shmini Atzeret')) return 'שמיני עצרת';
  if (hebrew?.includes('שמחת תורה') || title?.includes('Simchat Torah')) return 'שמחת תורה';
  return hebrew || title || '';
};

const useParshaList = (year) => {
  const [parshot, setParshot] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const cache = useRef({});

  useEffect(() => {
    if (!year) return;
    if (cache.current[year]) {
      setParshot(cache.current[year]);
      return;
    }
    setLoading(true);
    setFetchError(null);
    setParshot([]);
    // שליפת פרשות + חגים כדי לזהות שבתות מיוחדות (חול המועד / יום טוב)
    fetch(
      `https://www.hebcal.com/hebcal?v=1&cfg=json&year=${year}&yt=H&s=on&maj=on&i=on`
    )
      .then((r) => r.json())
      .then((data) => {
        const allItems = data.items || [];

        // פרשות רגילות
        const parshaItems = allItems
          .filter((i) => i.category === 'parashat')
          .sort((a, b) => (a.date < b.date ? -1 : 1))
          .map((i) => ({
            parshaName: i.hebrew || i.title,
            parshaDate: i.date,
            hebrewDate: i.hdate,
            isSpecial: false,
          }));

        // תאריכים עם פרשה רגילה
        const parshaDates = new Set(parshaItems.map(p => p.parshaDate));

        // חגים — קבץ לפי תאריך
        const holidays = allItems.filter(i => i.category === 'holiday');
        const holidaysByDate = {};
        holidays.forEach(h => {
          if (!holidaysByDate[h.date]) holidaysByDate[h.date] = [];
          holidaysByDate[h.date].push(h);
        });

        // מצא שבתות עם חג בלי פרשה
        const specialShabbatot = [];
        const processedDates = new Set();

        Object.entries(holidaysByDate).forEach(([date, dayHolidays]) => {
          const d = new Date(date + 'T12:00:00');
          if (d.getDay() !== 6) return;           // לא שבת
          if (parshaDates.has(date)) return;       // יש פרשה רגילה
          if (processedDates.has(date)) return;
          processedDates.add(date);

          // זיהוי חול המועד לפי:
          // 1. שדה yomtov: true = יו"ט, אחרת = חוה"מ
          // 2. כותרת עם CH"M / CH''M
          // 3. עברית עם חוה״מ / חול המועד
          const isAnyYomTov = dayHolidays.some(h => h.yomtov === true);
          const isCholHamoed = !isAnyYomTov || dayHolidays.some(h =>
            (h.title && (h.title.includes("(CH'") || h.title.includes('(CH"M)'))) ||
            (h.hebrew && (h.hebrew.includes('חוה״מ') || h.hebrew.includes('חול המועד')))
          );

          const primary = dayHolidays[0];
          const baseName = getHolidayBaseName(primary.hebrew, primary.title);

          // צור כותרת זהה ל-computeShabbatTitle
          const parshaName = isCholHamoed
            ? 'שבת חול המועד ' + baseName
            : 'שבת ו' + baseName;

          specialShabbatot.push({
            parshaName,
            parshaDate: date,
            hebrewDate: primary.hdate || '',
            isSpecial: true,
          });
        });

        // שלב ומיין לפי תאריך
        const combined = [...parshaItems, ...specialShabbatot]
          .sort((a, b) => (a.parshaDate < b.parshaDate ? -1 : 1));

        cache.current[year] = combined;
        setParshot(combined);
        setLoading(false);
      })
      .catch(() => {
        setFetchError('שגיאה בטעינת רשימת הפרשיות');
        setLoading(false);
      });
  }, [year]);

  return { parshot, loading, fetchError };
};

// ─── DrashotStep ───────────────────────────────────────────────────────────────

/**
 * DrashotStep
 * רשימת דרשות ליל שבת — כל דרשה משוייכת לפרשת שבוע ספציפית בשנה ספציפית
 *
 * Props:
 *   formData  { drashot: [{ text, parshaName, parshaDate }] }
 *   onChange  (partial) => void
 */
const DrashotStep = ({ formData, onChange }) => {
  const drashot = formData?.drashot || [];
  const [draft, setDraft] = useState(null);
  const [draftYear, setDraftYear] = useState(getCurrentHebrewYear());
  const [duplicateError, setDuplicateError] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);

  const isOpen = draft !== null;

  const { parshot, loading: parshotLoading, fetchError } = useParshaList(isOpen ? draftYear : null);

  const updateDrashot = (updated) => onChange({ drashot: updated });
  const removeDrasha = (index) => {
    updateDrashot(drashot.filter((_, i) => i !== index));
    if (editingIndex === index) closeDraft();
  };

  const openDraft = () => {
    setEditingIndex(null);
    setDraft({ text: '', parshaName: '', parshaDate: '' });
    setDraftYear(getCurrentHebrewYear());
    setDuplicateError(false);
  };

  const startEdit = (index) => {
    const d = drashot[index];
    setEditingIndex(index);
    setDraft({ text: d.text || '', parshaName: d.parshaName || '', parshaDate: d.parshaDate || '' });
    setDraftYear(getCurrentHebrewYear());
    setDuplicateError(false);
  };

  const closeDraft = () => {
    setDraft(null);
    setEditingIndex(null);
    setDuplicateError(false);
  };

  const handleParshaChange = (parshaDate) => {
    const match = parshot.find((p) => p.parshaDate === parshaDate);
    setDraft((d) => ({
      ...d,
      parshaDate,
      parshaName: match?.parshaName || '',
    }));
    setDuplicateError(false);
  };

  const saveDraft = () => {
    if (!draft?.text?.trim()) return;
    if (!draft?.parshaDate) return;
    // בדיקת כפילות — מתעלם מהפריט הנערך עצמו
    if (drashot.some((d, i) => d.parshaDate === draft.parshaDate && i !== editingIndex)) {
      setDuplicateError(true);
      return;
    }
    const entry = { text: draft.text.trim(), parshaName: draft.parshaName, parshaDate: draft.parshaDate };
    if (editingIndex !== null) {
      updateDrashot(drashot.map((d, i) => i === editingIndex ? entry : d));
    } else {
      updateDrashot([...drashot, entry]);
    }
    closeDraft();
  };

  const canSave = draft?.text?.trim() && draft?.parshaDate;

  return (
    <Box dir="rtl">
      <Typography sx={{ fontWeight: 700, color: navy, fontSize: '1rem', mb: 0.5 }}>
        דרשות ליל שבת
      </Typography>
      <Typography sx={{ fontSize: '0.8rem', color: '#6b7280', mb: 2, lineHeight: 1.5 }}>
        הוסף דרשות לפי פרשת שבוע או שבת מיוחדת (חול המועד, יום טוב). כל דרשה תוצג אוטומטית
        בשבת הרלוונטית בלבד — אם הפרשה או השבת המיוחדת אינה ברשימה, לא יוצג כלום.
      </Typography>

      <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
        {/* Right side (RTL): list */}
        <Box sx={{ flex: isOpen ? '0 0 40%' : 1, minWidth: 0, transition: 'flex 0.2s' }}>
          <Button
            startIcon={<Plus size={15} />}
            onClick={openDraft}
            disabled={isOpen}
            sx={{
              border: '1.5px dashed #d1d5db',
              borderRadius: '10px', color: '#6b7280', textTransform: 'none',
              fontSize: '0.8rem', px: 2, py: 0.75, width: '100%', mb: 1.5,
              '&:hover': { borderColor: navy, color: navy, bgcolor: '#f0f4fa' },
              '&.Mui-disabled': { borderColor: '#e5e7eb', color: '#c0c0c0' },
            }}
          >
            הוסף דרשה
          </Button>

          {drashot.length > 0 ? (
            <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 2, overflowY: 'auto', maxHeight: 'calc(70vh - 160px)' }}>
              {drashot.map((d, i) => {
                const isActive = editingIndex === i;
                return (
                  <Box
                    key={i}
                    sx={{
                      px: 1.5, py: 1,
                      display: 'flex', alignItems: 'center', gap: 0.75,
                      bgcolor: isActive ? '#eff6ff' : i % 2 === 0 ? '#fafafa' : 'white',
                      borderBottom: i < drashot.length - 1 ? '1px solid #f0f0f0' : 'none',
                      borderRight: isActive ? `3px solid ${navy}` : '3px solid transparent',
                      transition: 'all 0.15s',
                    }}
                  >
                    <GripVertical size={13} color="#d1d5db" style={{ flexShrink: 0 }} />
                    <Stack sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Typography noWrap sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#111827' }}>
                          {d.parshaName}
                        </Typography>
                        {d.parshaName.startsWith('שבת ') && (
                          <Box component="span" sx={{
                            display: 'inline-block',
                            bgcolor: '#b45309', color: 'white',
                            fontSize: '0.55rem', fontWeight: 700,
                            borderRadius: '4px', px: 0.5, py: 0.1,
                            lineHeight: 1.3, flexShrink: 0,
                          }}>
                            מיוחדת
                          </Box>
                        )}
                      </Stack>
                      <Stack direction="row" spacing={0.75}>
                        <Typography noWrap sx={{ fontSize: '0.7rem', color: '#9ca3af' }}>
                          {formatParshaDate(d.parshaDate)}
                        </Typography>
                        {d.text && (
                          <Typography noWrap sx={{ fontSize: '0.7rem', color: '#6b7280' }}>
                            — {textSnippet(d.text, 35)}
                          </Typography>
                        )}
                      </Stack>
                    </Stack>
                    <IconButton size="small" onClick={() => startEdit(i)}
                      sx={{ color: isActive ? navy : '#9ca3af', '&:hover': { color: navy }, p: 0.5 }}>
                      <Pencil size={13} />
                    </IconButton>
                    <IconButton size="small" onClick={() => removeDrasha(i)}
                      sx={{ color: '#9ca3af', '&:hover': { color: '#ef4444' }, p: 0.5 }}>
                      <Trash2 size={13} />
                    </IconButton>
                  </Box>
                );
              })}
            </Box>
          ) : !isOpen && (
            <Typography sx={{ textAlign: 'center', fontSize: '0.78rem', color: '#9ca3af', mt: 1 }}>
              אין דרשות — ניתן לדלג על שלב זה
            </Typography>
          )}
        </Box>

        {/* Left side (RTL): form */}
        {isOpen && (
          <Box key={editingIndex ?? 'new'} sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ border: '1px solid #bae6fd', borderRadius: 2, bgcolor: '#f0f9ff', p: 2 }}>
              <Stack spacing={2}>

                {/* Year selector */}
                <FormControl size="small" sx={{ maxWidth: 140 }}>
                  <InputLabel sx={{ fontSize: '0.82rem' }}>שנה</InputLabel>
                  <Select
                    label="שנה"
                    value={draftYear}
                    onChange={(e) => {
                      setDraftYear(Number(e.target.value));
                      setDraft((d) => ({ ...d, parshaDate: '', parshaName: '' }));
                      setDuplicateError(false);
                    }}
                    sx={{ ...inputSx, fontSize: '0.82rem', bgcolor: 'white' }}
                  >
                    {YEAR_OPTIONS.map((y) => (
                      <MenuItem key={y} value={y} sx={{ fontSize: '0.82rem' }}>{toGematria(y % 1000)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Parasha / special Shabbat selector */}
                <FormControl size="small" fullWidth>
                  <InputLabel sx={{ fontSize: '0.82rem' }}>שבת / פרשה</InputLabel>
                  {parshotLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <CircularProgress size={16} />
                      <Typography sx={{ fontSize: '0.78rem', color: '#6b7280' }}>טוען פרשיות…</Typography>
                    </Box>
                  ) : fetchError ? (
                    <Typography sx={{ fontSize: '0.78rem', color: '#ef4444', mt: 1 }}>{fetchError}</Typography>
                  ) : (
                    <Select
                      label="שבת / פרשה"
                      value={draft.parshaDate || ''}
                      onChange={(e) => handleParshaChange(e.target.value)}
                      sx={{ ...inputSx, fontSize: '0.82rem', bgcolor: 'white' }}
                    >
                      <MenuItem value="" disabled sx={{ fontSize: '0.82rem', color: '#9ca3af' }}>בחר פרשה או שבת מיוחדת…</MenuItem>
                      {parshot.map((p) => (
                        <MenuItem
                          key={p.parshaDate}
                          value={p.parshaDate}
                          disabled={drashot.some((dd, idx) => dd.parshaDate === p.parshaDate && idx !== editingIndex)}
                          sx={{
                            fontSize: '0.82rem',
                            ...(p.isSpecial && {
                              bgcolor: '#fef3c7',
                              fontWeight: 700,
                              '&:hover': { bgcolor: '#fde68a' },
                              '&.Mui-disabled': { bgcolor: '#f5f0e0', opacity: 0.6 },
                            }),
                          }}
                        >
                          {p.isSpecial && (
                            <Box component="span" sx={{
                              display: 'inline-block',
                              bgcolor: '#b45309', color: 'white',
                              fontSize: '0.62rem', fontWeight: 700,
                              borderRadius: '4px', px: 0.7, py: 0.15,
                              ml: 1, verticalAlign: 'middle', lineHeight: 1.3,
                            }}>
                              מיוחדת
                            </Box>
                          )}
                          {p.parshaName} — {p.hebrewDate ? formatHebrewDate(p.hebrewDate) : formatParshaDate(p.parshaDate)}{p.hebrewDate ? ` (${formatParshaDate(p.parshaDate)})` : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                </FormControl>

                {duplicateError && (
                  <Typography sx={{ fontSize: '0.78rem', color: '#ef4444' }}>
                    פרשה זו כבר קיימת ברשימה
                  </Typography>
                )}

                {/* Text field */}
                <TextField
                  size="small"
                  label="תוכן הדרשה"
                  placeholder='לדוגמה: הרב ישראל ישראלי ידרוש על פרשת השבוע'
                  value={draft.text}
                  onChange={(e) => setDraft((d) => ({ ...d, text: e.target.value }))}
                  multiline
                  minRows={2}
                  fullWidth
                  required
                  sx={{ ...inputSx, '& .MuiInputLabel-root': { fontSize: '0.82rem' }, bgcolor: 'white' }}
                />

                {/* Actions */}
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button
                    onClick={closeDraft}
                    sx={{ color: '#6b7280', textTransform: 'none', fontSize: '0.8rem' }}
                  >
                    ביטול
                  </Button>
                  <Button
                    variant="contained"
                    onClick={saveDraft}
                    disabled={!canSave}
                    sx={{
                      bgcolor: navy, color: 'white', textTransform: 'none', fontSize: '0.8rem',
                      boxShadow: 'none', borderRadius: '8px',
                      '&:hover': { bgcolor: '#152844', boxShadow: 'none' },
                    }}
                  >
                    {editingIndex !== null ? 'עדכן' : 'הוסף'}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DrashotStep;
